import { Box, Theme, Typography } from '@mui/material';
import { NavLink, NavLinkProps } from 'react-router-dom';
import {
  getSidenavHoverBackground,
  getSidenavIconColor,
  getSidenavSeparatorColor,
  getSidenavTextColor,
} from '../sidenav-colors';

interface PopoverTitleProps {
  label: string;
  showBorder?: boolean;
  /** When set, the title navigates like the rail icon (leaf collapsed flyouts). */
  to?: NavLinkProps['to'];
  /** Fires after a navigable title is clicked (e.g. close the flyout). */
  onClick?: () => void;
}

/**
 * Title row for sidenav collapsed flyouts.
 * Optional `to` makes the header a link so leaf items match icon navigation.
 */
export function PopoverTitle({ label, showBorder = false, to, onClick }: PopoverTitleProps) {
  const content = (
    <Typography
      component="div"
      sx={(theme) => ({
        fontSize: '0.8rem',
        color: getSidenavIconColor(theme),
        textTransform: 'uppercase',
      })}
    >
      {label}
    </Typography>
  );

  const sharedSx = (theme: Theme) => ({
    px: 2.5,
    py: 1.5,
    display: 'block',
    textDecoration: 'none',
    color: 'inherit',
    ...(showBorder && {
      borderBottom: `1px solid ${getSidenavSeparatorColor(theme)}`,
    }),
  });

  if (to) {
    return (
      <Box
        component={NavLink}
        to={to}
        onClick={onClick}
        sx={(theme) => ({
          ...sharedSx(theme),
          cursor: 'pointer',
          transition: theme.transitions.create(['background-color'], {
            duration: theme.transitions.duration.shorter,
          }),
          '&:hover, &:focus': {
            background: getSidenavHoverBackground(theme),
            color: getSidenavTextColor(theme),
          },
        })}
      >
        {content}
      </Box>
    );
  }

  return <Box sx={(theme) => sharedSx(theme)}>{content}</Box>;
}
